/* globals __CLIENT__ */

import React, { PropTypes, PureComponent } from 'react';
import update from 'immutability-helper';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import sport from '@letsdothis/reference-data/src/sport.json';

import { breakpointMD } from '../../base-styles/vars';
import { searches } from '../../utils/localStorage';
import { getLocalStorage, setLocalStorage, stringifyQS } from '../../utils';
import HomepageSearch from '../../components/HomepageSearch';

class HomepageSearchContainer extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      editing: '',
      fetching: false,
      values: props.values,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillReceiveProps(nextProps) {
    const { values } = this.props;

    if (values.sport !== nextProps.values.sport ||
      values.sportDistance !== nextProps.values.sportDistance ||
      values.location !== nextProps.values.location) {
      this.setState({ values: nextProps.values });
    }
  }

  getValues = () => {
    const lastSearch = JSON.parse(getLocalStorage(searches.last.key));
    if (lastSearch) {
      return {
        sport: lastSearch.sport,
        sportDistance: lastSearch.sportDistance,
        geolocation: false,
        ...(lastSearch.locationName && {
          location: {
            name: lastSearch.locationName,
            ...(lastSearch.locationLat ? {
              types: ['locality', 'political'],
              geometry: {
                location: {
                  lat: () => lastSearch.locationLat,
                  lng: () => lastSearch.locationLong,
                },
              },
            } : {
              types: ['bounds'],
              geometry: {
                viewport: {
                  getNorthEast: () => ({
                    lat: () => lastSearch.viewportNorthEastLat,
                    lng: () => lastSearch.viewportNorthEastLong,
                  }),
                  getSouthWest: () => ({
                    lat: () => lastSearch.viewportSouthWestLat,
                    lng: () => lastSearch.viewportSouthWestLong,
                  }),
                },
              },
            }),
          },
          locationSearchRadius: lastSearch.locationSearchRadius,
        }),
      };
    }
    return this.props.geoLocation && this.props.geoLocation.city
      ? ({
        sport: 3,
        sportDistance: [170],
        geolocation: false,
        location: {
          geometry: {
            location: {
              lat: () => this.props.geoLocation.latitude,
              lng: () => this.props.geoLocation.longitude,
            },
          },
          name: this.props.geoLocation.city,
          types: ['locality', 'political'],
        },
        locationSearchRadius:
          this.props.geoLocation.locationSearchRadius ||
          this.props.countryCode === 'US' ? 200 : 50,
      }) : ({
        sport: 3,
        sportDistance: [170],
        geolocation: false,
        location: {
          geometry: {
            location: {
              lat: () => 51.1,
              lng: () => 0.0,
            },
          },
          name: 'London',
          types: ['locality', 'political'],
        },
        locationSearchRadius: 50,
      });
  }

  fetchData = () => {
    const data = sport;
    this.setState({
      // Reduce sports to ID label pairs
      // sports: data.map(({ id, label }) => ({ id, label })),
      sports: data
        .filter(({ id }) => id !== 86)
        .map(({ id, label, noun }) => ({ id, label, noun })),
      // Reduce distances to flat array of ID label pairs with parent ID
      distances:
        data.filter(({ distances }) => distances.length)
          .map(({ id: parent, distances }) =>
            distances.map(({ id, label }) => ({ id, label, parent })))
          .reduce((flatten, distances) => flatten.concat(distances), []),
      values: { ...this.getValues() },
    });
  }

  handleChange = (event) => {
    // Delegate field change events
    // Reducer style, this might later end up in Redux store
    switch (event.target.name) {
      case 'geolocation':
        this.handleChangeGeolocation(event);
        break;
      case 'location':
      case 'locationSearchRadius':
        this.handleChangeLocation(event);
        break;
      case 'maxDate':
      case 'minDate':
        this.handleChangeDate(event);
        break;
      case 'sport':
        this.handleChangeSport(event);
        this.handleClose();
        break;
      default:
        // Catch dynamically named fields e.g. sportDistance-1
        this.handleChangeCheckbox(event);
        break;
    }
  }

  handleChangeCheckbox = (event) => {
    const { target } = event;
    const [name, id] = target.name.split('-');
    const value = parseInt(id, 10);
    if (target.checked) {
      // Push ID into array of IDs
      this.setState(state =>
        update(state, {
          values: {
            [name]: { $set: (state.values[name] || []).concat([value]) },
          },
        }),
      );
    } else {
      // Splice ID out of array of IDs
      this.setState(state =>
        update(state, {
          values: {
            [name]: { $splice: [[state.values[name].indexOf(value), 1]] },
          },
        }),
      );
    }
  }

  handleChangeGeolocation = (event) => {
    const { value } = event.target;
    this.setState(state =>
      update(state, {
        values: {
          geolocation: { $set: true },
          location: { $set: value },
          // ..and set default proximity unless already set
          ...(!state.values.locationSearchRadius)
            && { locationSearchRadius: { $set: 50 } },
        },
      }));
  };

  handleChangeLocation = (event) => {
    // Handle location place and proximity
    const { name, value } = event.target;
    this.setState(state =>
      update(state,
        name === 'location'
          ? {
            values: {
              // Reset geolocation if we're using a place instead
              geolocation: { $set: false },
              [name]: { $set: value },
              // ..and set default proximity unless already set
              ...(!state.values.locationSearchRadius && value)
                && { locationSearchRadius: { $set: 50 } },
            },
          }
          : {
            values: {
              [name]: { $set: parseInt(value, 10) },
            },
          },
      ));
  };

  handleChangeSport = (event) => {
    const value = parseInt(event.target.value, 10);
    this.setState(state =>
      update(state, {
        // Set new sport and nuke sport-related choices
        values: {
          sport: { $set: value },
          sportDistance: { $set: undefined },
        },
      }));
  }

  handleEdit = (event) => {
    if (this.state.editing && (this.state.editing !== event.target.name)) {
      this.handleSubmit();
    }

    if (__CLIENT__ && !window.matchMedia(`(min-width: ${breakpointMD})`).matches) {
      document.body.querySelector('main').style.position = 'relative';
      document.body.querySelector('main').style.zIndex = 1050;
    }
    this.setState({
      editing: event.target.name,
    });
  }

  handleFetched = () => {
    this.setState({ fetching: false });
  }

  handleFetching = () => {
    this.setState({ fetching: true });
  }

  handleSubmit = () => {
    if (this.state.fetching) {
      //
      return;
    }
    this.setState({
      editing: undefined,
    });
    this.handleFiltersSubmit(this.cleanValues(this.state.values));
  }

  handleToggleClick = () => {
    this.toggleOpen();
  }

  toggleOpen = () => {
    this.setState(({ isOpen }) => ({
      isOpen: !isOpen,
    }));
  }

  toggleLabels = () => {
    this.setState(({ conversational }) => ({
      conversational: !conversational,
    }));
  }

  handleFiltersSubmit = (formValues) => {
    const queryString = stringifyQS(formValues);
    window.scrollTo(0, 0); // TODO: This jumps to the top of the page on every search
    // Save search to local storage
    setLocalStorage(searches.last.key, JSON.stringify(formValues));
    this.props.router.push(`/s?${queryString}`);
  }

  handleClose = () => {
    document.body.querySelector('main').style.zIndex = 1;
    this.setState({
      editing: '',
    });
  }

  cleanValues = values =>
    // The values object represents the current state of selections,
    // not the computed values to be submitted, this function determines those choices
    Object.keys(values)
      // Fields to remove, e.g. slugs (using ID instead), non-search params e.g. geolocation
      .filter(key => ![
        'locationSlug',
        'geolocation',
        'locationName',
        'locationLat',
        'locationLong',
        'viewportNorthEastLat',
        'viewportNorthEastLong',
        'viewportSouthWestLat',
        'viewportSouthWestLong'].includes(key))
      .reduce((obj, key) => {
        // Location is a complex object, we only need the lat and long params
        if (key === 'location' && values[key]) {
          const place = values[key];
          // These location types have bounds search
          if (['country', 'administrative_area_level_1', 'administrative_area_level_2', 'bounds'].includes(place.types[0])) {
            return {
              ...obj,
              locationName: place.name,
              viewportNorthEastLat: place.geometry.viewport.getNorthEast().lat(),
              viewportNorthEastLong: place.geometry.viewport.getNorthEast().lng(),
              viewportSouthWestLat: place.geometry.viewport.getSouthWest().lat(),
              viewportSouthWestLong: place.geometry.viewport.getSouthWest().lng(),
            };
          }
          // Otherwise use a single-point search
          return {
            ...obj,
            locationName: place.name,
            locationLat: place.geometry.location.lat(),
            locationLong: place.geometry.location.lng(),
          };
        }
        // Radius is only valid for single-point queries
        if (key === 'locationSearchRadius'
            && (['country', 'administrative_area_level_1', 'administrative_area_level_2'].includes(values.location && values.location.types[0]) || !values.location)) {
          return {
            ...obj,
            [key]: undefined,
          };
        }
        // Everything else is fine, but nullify empty strings for brevity
        return {
          ...obj,
          [key]: values[key] === '' ? null : values[key],
        };
      }, {});

  render() {
    const {
      distances,
      sports,
      values: {
        sport: selectedSport,
      },
    } = this.state;

    if (!sports || !distances) {
      // TODO: Ensure these are present with pre-fetch
      return null;
    }

    // Distances are conditional based on current sport choice
    const filteredDistances = selectedSport
      ? distances.filter(distance => distance.parent === selectedSport)
      : [];

    return (
      <HomepageSearch
        {...this.props}
        {...this.state}
        onChange={this.handleChange}
        onClose={this.handleClose}
        onEdit={this.handleEdit}
        onFetched={this.handleFetched}
        onFetching={this.handleFetching}
        onSubmit={this.handleSubmit}
        onToggleClick={this.handleToggleClick}
        distances={filteredDistances.length ? filteredDistances : undefined}
      />
    );
  }
}

HomepageSearchContainer.propTypes = {
  /**
   * The code representing the country in which the session is taking place
   */
  countryCode: PropTypes.string,
  /**
   * Object describing the user's location
   */
  geoLocation: PropTypes.shape({
    city: PropTypes.string,
    latitude: PropTypes.string,
    longitude: PropTypes.string,
    locationSearchRadius: PropTypes.number,
  }),
  /**
   * Object to populate form selections.
   */
  values: PropTypes.shape(),
};

HomepageSearchContainer.defaultProps = {
  values: {},
  geoLocation: {},
  isLoggedIn: false,
  countryCode: undefined,
};

const mapStateToProps = state => ({
  countryCode: state.countryCode,
});

const HomepageSearchContainerWithRedux = connect(mapStateToProps)(HomepageSearchContainer); // eslint-disable-line no-class-assign, max-len

// Wrap with [react-router](https://github.com/ReactTraining/react-router/blob/master/docs/API.md#withroutercomponent-options)
const HomepageSearchContainerWithRouter = withRouter(HomepageSearchContainerWithRedux);

export default HomepageSearchContainerWithRouter;



// WEBPACK FOOTER //
// ./app/src/containers/HomepageSearch/index.jsx